import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Flex from '@utilities/components/Layout/components/Flex';
import { HelpIcon, IconButtonV2, toast } from '@utilities';

import { createChatSession, updateFlowStateTopicIds } from '@api/narrative-projects';
import Narratives from '../Narratives';
import NarrativesCanvas from './components/NarrativesCanvas';
import NarrativesChatLandingPage from './components/NarrativesChatLandingPage';
import NarrativesChatWindow from './components/NarrativesChatWindow';
import NarrativesNavigation from './components/NarrativesNavigation';
import NarrativesTopicsWindow from './components/NarrativesTopicsWindow';

import { HELP_CENTER_BASE_URL } from '../Narratives/utilities/constants';
import { DEFAULT_DRAFT_NARRATIVE_NAME } from './utilities/constants';

import styles from './_index.module.scss';

const PAGES = {
  CHAT: 'chat',
  LANDING: 'landing',
  TEMPLATES: 'templates',
  TOPICS: 'topics',
};

const WORKSPACE_PAGES = new Set([PAGES.CHAT, PAGES.TOPICS]);

const buildInitialSessionState = () => ({
  chatSessionId: null,
});

const NarrativesChat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePage, setActivePage] = useState(PAGES.LANDING);
  const [flowId, setFlowId] = useState(null);
  const [draftFlowState, setDraftFlowState] = useState(null);
  const [initialInput, setInitialInput] = useState('');
  const [sessionState, setSessionState] = useState(buildInitialSessionState);
  const [selectedTopicCards, setSelectedTopicCards] = useState([]);
  const [chatSnapshot, setChatSnapshot] = useState(null);
  const isCreatingChatSessionRef = useRef(false);

  const isLanding = activePage === PAGES.LANDING;
  const isTemplates = activePage === PAGES.TEMPLATES;
  const isChat = activePage === PAGES.CHAT;
  const isTopics = activePage === PAGES.TOPICS;
  const shouldDisableBodyScroll = !isLanding && !isTemplates;
  const shouldShowWorkspace = isChat || isTopics;
  const hasSelectedTopics = selectedTopicCards.length > 0;
  const hasWorkspaceContent = Boolean(flowId) || Boolean(draftFlowState) || hasSelectedTopics;
  const shouldAllowCanvasHydrationOnMount = !hasWorkspaceContent;
  const chatSessionIdFromUrl = searchParams.get('sessionId');

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(draftFlowState?.components)) return [];
    return draftFlowState.components.filter((componentId) => componentId != null);
  }, [draftFlowState]);

  const selectedTopicIds = useMemo(() => {
    const manualIds = selectedTopicCards.map((topic) => topic.id).filter((id) => id != null);
    if (!flowStateTopicIds.length) return manualIds;
    return Array.from(new Set([...flowStateTopicIds, ...manualIds]));
  }, [flowStateTopicIds, selectedTopicCards]);

  const replaceChatSessionQueryParam = useCallback(
    (chatSessionId) => {
      const params = new URLSearchParams(searchParams);
      if (chatSessionId) {
        params.set('sessionId', chatSessionId);
      } else {
        params.delete('sessionId');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const resetSessionState = useCallback(() => {
    setSessionState(buildInitialSessionState());
    setChatSnapshot(null);
    setFlowId(null);
    setDraftFlowState(null);
    setSelectedTopicCards([]);
    replaceChatSessionQueryParam(null);
  }, [replaceChatSessionQueryParam]);

  const handleChatSessionReady = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      setSessionState((prev) => {
        if (prev.chatSessionId === sessionId) return prev;
        return { ...prev, chatSessionId: sessionId };
      });
      replaceChatSessionQueryParam(sessionId);
      setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
    },
    [replaceChatSessionQueryParam]
  );

  const handleFlowSelected = useCallback((nextFlowId) => {
    setFlowId(nextFlowId);
  }, []);

  const handleFlowStateChange = useCallback((nextFlowState) => {
    setDraftFlowState(nextFlowState);
  }, []);

  const ensureChatSession = useCallback(async () => {
    if (sessionState.chatSessionId || isCreatingChatSessionRef.current) return;

    isCreatingChatSessionRef.current = true;
    try {
      const sessionId = await createChatSession();
      if (!sessionId) throw new Error('Chat service did not return a session identifier.');
      handleChatSessionReady(sessionId);
    } catch (error) {
      console.error('Failed to create chat session from topics workspace', error);
      toast('We could not start your project. Please try again.', { status: 'error' });
    } finally {
      isCreatingChatSessionRef.current = false;
    }
  }, [handleChatSessionReady, sessionState.chatSessionId]);

  const handleToggleTopicCard = useCallback(
    async (topic, shouldAdd) => {
      if (!topic?.id) return;

      const topicId = topic.id;

      if (shouldAdd) {
        setSelectedTopicCards((prev) => {
          const exists = prev.some((item) => item.id === topicId);
          if (exists) return prev;

          const nextTopic = {
            id: topicId,
            name: topic.name,
            vizType: topic.vizType,
          };

          return [...prev, nextTopic];
        });
        return;
      }

      setSelectedTopicCards((prev) => prev.filter((item) => item.id !== topicId));

      const currentComponentIds = Array.isArray(draftFlowState?.components)
        ? draftFlowState.components
        : [];
      const nextComponentIds = currentComponentIds.filter((id) => String(id) !== String(topicId));

      if (nextComponentIds.length === currentComponentIds.length) return;

      const previousFlowState = draftFlowState;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, components: nextComponentIds }
        : {
            components: nextComponentIds,
            flowId,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
          };

      setDraftFlowState(optimisticFlowState);

      if (!sessionState.chatSessionId) return;

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            flowId,
            topicIds: nextComponentIds,
          })) || optimisticFlowState;

        setDraftFlowState(updatedFlowState);
      } catch (error) {
        console.error('Failed to remove topic card from draft flow state', error);
        setDraftFlowState(previousFlowState);
        toast('We could not remove that topic card. Please try again.', { status: 'error' });
      }
    },
    [draftFlowState, flowId, sessionState.chatSessionId]
  );

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage === 'new-project') {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      if (nextPage === PAGES.LANDING) {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      const nextShowsWorkspace = WORKSPACE_PAGES.has(nextPage);
      if (shouldShowWorkspace && !nextShowsWorkspace) {
        resetSessionState();
      }

      setActivePage(nextPage);
    },
    [resetSessionState, shouldShowWorkspace]
  );

  const handleChatMessageSent = useCallback(() => {
    setInitialInput('');
  }, []);

  const handleLandingSubmit = (input) => setInitialInput(input);
  const handleHelpClick = () => {
    window.open(HELP_CENTER_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleChatStateSnapshot = useCallback((snapshot) => {
    setChatSnapshot(snapshot);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', shouldDisableBodyScroll);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [shouldDisableBodyScroll]);

  useEffect(() => {
    if (chatSessionIdFromUrl) {
      setSessionState((prev) => {
        if (prev.chatSessionId === chatSessionIdFromUrl) return prev;
        return { ...prev, chatSessionId: chatSessionIdFromUrl };
      });
      if (activePage === PAGES.LANDING) {
        setActivePage(PAGES.CHAT);
      }
      return;
    }

    if (!sessionState.chatSessionId) return;
    resetSessionState();
    setActivePage(PAGES.LANDING);
  }, [activePage, chatSessionIdFromUrl, resetSessionState, sessionState.chatSessionId]);

  useEffect(() => {
    if (shouldShowWorkspace) return;
    setFlowId(null);
    setDraftFlowState(null);
  }, [shouldShowWorkspace]);

  useEffect(() => {
    if (!hasSelectedTopics || sessionState.chatSessionId) return;
    ensureChatSession();
  }, [ensureChatSession, hasSelectedTopics, sessionState.chatSessionId]);

  return (
    <>
      <Flex className={styles['narratives-root']} gap="none">
        <NarrativesNavigation activePage={activePage} onClick={handlePageChange} />

        {isLanding && (
          <>
            <NarrativesChatLandingPage
              onNavigation={handlePageChange}
              onSubmit={handleLandingSubmit}
            />

            <IconButtonV2
              aria-label="Help"
              className={styles['help-button']}
              icon={<HelpIcon />}
              onClick={handleHelpClick}
              size="small"
              tooltip="Help"
              tooltipProps={{ placement: 'left' }}
              variant="subtle"
            />
          </>
        )}

        {isTemplates && <Narratives />}

        {shouldShowWorkspace && (
          <div className={styles['narratives-chat-shell']}>
            <div className={styles['narratives-chat-content']}>
              <div className={styles['narratives-chat-window']}>
                {isChat && (
                  <NarrativesChatWindow
                    allowCanvasHydrationOnMount={shouldAllowCanvasHydrationOnMount}
                    chatSessionId={sessionState.chatSessionId}
                    initialErrorMessage={chatSnapshot?.errorMessage}
                    initialInput={initialInput}
                    initialInputValue={chatSnapshot?.inputValue}
                    initialMessages={chatSnapshot?.messages}
                    key={sessionState.chatSessionId || 'draft'}
                    onChatSessionReady={handleChatSessionReady}
                    onFlowSelected={handleFlowSelected}
                    onFlowStateChange={handleFlowStateChange}
                    onMessageSent={handleChatMessageSent}
                    onStateSnapshot={handleChatStateSnapshot}
                    syncCanvasOnMount={!chatSnapshot?.wasHydrated}
                  />
                )}
                {isTopics && (
                  <NarrativesTopicsWindow
                    onToggleTopicCard={handleToggleTopicCard}
                    selectedTopicIds={selectedTopicIds}
                  />
                )}
              </div>

              <div className={styles['narratives-chat-canvas']}>
                <NarrativesCanvas
                  chatSessionId={sessionState.chatSessionId}
                  flowId={flowId}
                  flowState={draftFlowState}
                  onFlowSelected={handleFlowSelected}
                  onFlowStateChange={handleFlowStateChange}
                  onSelectedTopicsChange={setSelectedTopicCards}
                  selectedTopicCards={selectedTopicCards}
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
    </>
  );
};

export default NarrativesChat;
